import React from "react";

import {
  WorkOSAuthProvider,
  useWorkOSAuth,
} from "@worktango/d2c-service/workOSReact";
import { OpenFeatureProvider } from "@worktango/feature-flags-react";

import { ToastProvider } from "@kazoohr/confetti";
import {
  ApolloClient,
  InMemoryCache,
  BatchHttpLink,
  ApolloLink,
  onError,
} from "@kazoohr/graphql";
import { Router } from "@kazoohr/router";

import { getClientId, setClientId } from "./clientId";
import { ConstellationWrapper } from "./components/base/ConstellationWrapper";
import { InternationalizationWrapper } from "./components/base/InternationalizationWrapper";
import { ConstellationApolloProviders } from "./ConstellationApolloProviders";

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <Router>
      <OpenFeatureProvider>
        <WorkOSAuthProvider>
          <ToastProvider text={{ closeButtonMouseover: "Close" }}>
            <ApolloProviders>
              <InternationalizationWrapper>
                <ConstellationWrapper>{children}</ConstellationWrapper>
              </InternationalizationWrapper>
            </ApolloProviders>
          </ToastProvider>
        </WorkOSAuthProvider>
      </OpenFeatureProvider>
    </Router>
  );
}

/**
 * Check if an error is retryable (5xx errors, network failures)
 */
function isRetryableError(status: number): boolean {
  // 5xx server errors are retryable
  if (status >= 500 && status < 600) {
    return true;
  }
  // 429 Too Many Requests is retryable
  if (status === 429) {
    return true;
  }
  return false;
}

/**
 * Calculate exponential backoff delay with jitter
 */
function calculateBackoff(attempt: number): number {
  const initialDelay = 300;
  const maxDelay = 10000;
  // Exponential backoff: initial * 2^attempt
  const exponentialDelay = Math.min(
    initialDelay * Math.pow(2, attempt),
    maxDelay
  );
  // Add jitter: random value between 0.5x and 1.5x the delay
  const jitter = 0.5 + Math.random();
  return Math.floor(exponentialDelay * jitter);
}

/**
 * Check if an error message indicates a network failure that should be retried
 */
function isNetworkError(message: string): boolean {
  return (
    message.includes("fetch failed") ||
    message.includes("Failed to fetch") ||
    message.includes("NetworkError")
  );
}

/**
 * Wait for the backoff delay before retrying
 */
function waitForBackoff(attempt: number): Promise<void> {
  const delay = calculateBackoff(attempt);
  return new Promise((resolve) => setTimeout(resolve, delay));
}

/**
 * Check if the request body contains a GraphQL mutation (which should not be retried)
 */
function isMutationRequest(init?: RequestInit): boolean {
  if (!init?.body || typeof init.body !== "string") {
    return false;
  }
  try {
    const parsed = JSON.parse(init.body);
    const query = parsed.query?.trim() || "";
    return query.startsWith("mutation");
  } catch {
    return false;
  }
}

/**
 * Check if an HTTP error response should trigger a retry
 */
async function shouldRetryResponse(
  response: Response,
  attempt: number,
  maxAttempts: number
): Promise<boolean> {
  if (response.ok || !isRetryableError(response.status)) {
    return false;
  }
  if (attempt >= maxAttempts - 1) {
    return false;
  }
  // Check if it's an HTML error page (like a gateway error)
  const text = await response
    .clone()
    .text()
    .catch(() => "");
  return text.startsWith("<");
}

/**
 * Custom fetch with retry logic for transient errors.
 * Only retries queries, not mutations (to avoid duplicate side effects).
 */
// eslint-disable-next-line complexity
async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const initWithClientId = addClientIdHeader(init);

  // Never retry mutations - they could cause duplicate side effects
  if (isMutationRequest(initWithClientId)) {
    return fetchAndSyncClientId(input, initWithClientId);
  }

  const maxAttempts = 5;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await fetchAndSyncClientId(input, initWithClientId);

      if (await shouldRetryResponse(response, attempt, maxAttempts)) {
        await waitForBackoff(attempt);
        continue;
      }

      return response;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (isNetworkError(lastError.message) && attempt < maxAttempts - 1) {
        await waitForBackoff(attempt);
        continue;
      }

      throw lastError;
    }
  }

  // istanbul ignore next - defensive unreachable code, loop always throws on failure
  throw lastError || new Error("Max retry attempts reached");
}

/**
 * Adds the x-client-id header from localStorage so the server can identify
 * the participant even when cookies are blocked.
 */
function addClientIdHeader(init?: RequestInit): RequestInit {
  const clientId = getClientId();
  const headers = new Headers(init?.headers);
  headers.set("x-client-id", clientId);
  return { ...init, headers };
}

/**
 * Calls fetch and syncs the server-assigned client ID back to localStorage.
 * This handles migration: existing cookie-based users get their ID stored
 * locally on the first response.
 */
async function fetchAndSyncClientId(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const response = await fetch(input, init);
  const serverClientId = response.headers?.get?.("x-client-id");
  if (serverClientId) {
    setClientId(serverClientId);
  }
  return response;
}

function ApolloProviders({ children }: { children: React.ReactNode }) {
  const { signIn } = useWorkOSAuth();

  const client = React.useMemo(() => {
    const httpLink = new BatchHttpLink({
      uri: "/api",
      fetch: fetchWithRetry,
      fetchOptions: {
        credentials: "include",
      },
    });

    // Error link to handle authentication errors
    const errorLink = onError(
      // eslint-disable-next-line complexity
      ({ graphQLErrors, networkError }) => {
        // Check for authentication errors in GraphQL errors
        if (graphQLErrors) {
          for (const err of graphQLErrors) {
            if (
              err.extensions?.code === "UNAUTHENTICATED" ||
              err.message.includes("Not authenticated")
            ) {
              // eslint-disable-next-line no-console
              console.error(
                "Authentication error detected, redirecting to login"
              );
              signIn({ returnTo: window.location.pathname });
              return;
            }
          }
        }

        // Check for 401/403 network errors
        if (networkError && "statusCode" in networkError) {
          const statusCode = networkError?.statusCode;
          if (statusCode === 401 || statusCode === 403) {
            console.error("Auth error from network, redirecting to login"); // eslint-disable-line no-console
            signIn({ returnTo: window.location.pathname });
            return;
          }
        }
      }
    );

    return new ApolloClient({
      link: ApolloLink.from([errorLink, httpLink]),
      cache: new InMemoryCache(),
    });
  }, [signIn]);

  return (
    <ConstellationApolloProviders client={client}>
      {children}
    </ConstellationApolloProviders>
  );
}
